
import React, { useState, useMemo, useEffect } from 'react';
import { Calendar as CalendarIcon, Clock, CheckCircle, ChevronLeft, ChevronRight, Phone, Loader2, Sparkles, Info, AlertTriangle, Wallet, Mail, ExternalLink, XCircle, PlusCircle, Tag, ShieldCheck, Video, MessageSquare, PhoneCall } from 'lucide-react';
import { BookingState, TimeSlot, Service, MemberRecord } from '../types';
import { submitBooking } from '../services/geminiService';
import { SERVICES } from './Services';
import PayPalButtons from './PayPalButtons';

const TIME_SLOTS: TimeSlot[] = [
  { time: '10:00 AM', available: true },
  { time: '11:30 AM', available: true },
  { time: '01:00 PM', available: false },
  { time: '02:30 PM', available: true },
  { time: '04:00 PM', available: true },
  { time: '05:30 PM', available: true },
];

const RECIPIENT_EMAIL = "allie.pakele@gmail.com";

interface BookingProps {
  initialServiceIds?: string[];
  memberProfile?: MemberRecord | null;
}

const Booking: React.FC<BookingProps> = ({ initialServiceIds = [], memberProfile }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewDate, setViewDate] = useState(new Date()); 
  const today = new Date();
  
  const [booking, setBooking] = useState<BookingState>({
    serviceIds: initialServiceIds,
    date: null,
    time: null,
    name: memberProfile?.name || '',
    email: memberProfile?.email || '',
    phone: '',
    commMethod: 'video'
  });

  useEffect(() => {
    if (initialServiceIds.length > 0) {
      setBooking(prev => ({ 
        ...prev, 
        serviceIds: Array.from(new Set([...prev.serviceIds, ...initialServiceIds])) 
      }));
    }
  }, [initialServiceIds]);

  useEffect(() => {
    if (memberProfile) {
      setBooking(prev => ({
        ...prev,
        name: prev.name || memberProfile.name,
        email: prev.email || memberProfile.email || ''
      }));
    }
  }, [memberProfile]);

  const selectedServices = useMemo(() => {
    return SERVICES.filter(s => booking.serviceIds.includes(s.id));
  }, [booking.serviceIds]);

  const discountPercent = useMemo(() => {
    if (!memberProfile) return 0;
    if (memberProfile.tier === 'Solar Initiate') return 0.05;
    if (memberProfile.tier === 'Galactic Master') return 0.10;
    return 0;
  }, [memberProfile]);

  const subtotal = useMemo(() => selectedServices.reduce((sum, s) => sum + s.price, 0), [selectedServices]);
  const finalPrice = useMemo(() => subtotal * (1 - discountPercent), [subtotal, discountPercent]);

  const monthData = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const monthName = viewDate.toLocaleString('default', { month: 'long' }).toUpperCase();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayIndex = new Date(year, month, 1).getDay();
    return { year, month, monthName, daysInMonth, firstDayIndex, daysArray: Array.from({ length: daysInMonth }, (_, i) => i + 1) };
  }, [viewDate]);

  const handlePaymentSuccess = async (details: any) => {
    setIsSubmitting(true);
    try {
      await submitBooking(booking, details);
      setStep(4);
    } catch (error) {
      alert("Payment verified but record failed. Please contact Allie.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleService = (id: string) => {
    setBooking(prev => ({
      ...prev,
      serviceIds: prev.serviceIds.includes(id) 
        ? prev.serviceIds.filter(sid => sid !== id)
        : [...prev.serviceIds, id]
    }));
  };

  const isToday = (day: number) => today.getDate() === day && today.getMonth() === monthData.month && today.getFullYear() === monthData.year;
  const isSelected = (day: number) => booking.date?.getDate() === day && booking.date?.getMonth() === monthData.month && booking.date?.getFullYear() === monthData.year;
  const isPast = (day: number) => new Date(monthData.year, monthData.month, day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());

  return (
    <div id="booking" className="animate-fade-in relative z-10">
      <div className="bg-slate-950/80 backdrop-blur-xl border border-indigo-900/50 p-8 md:p-12 shadow-2xl relative overflow-hidden rounded-sm">
        
        {step < 4 && (
          <div className="mb-12">
            <div className="flex justify-between items-center mb-8">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border transition-all duration-500 ${step >= s ? 'bg-amber-500 border-amber-500 text-slate-950 shadow-[0_0_10px_rgba(245,158,11,0.5)]' : 'border-indigo-800 text-indigo-400'}`}>
                    {s}
                  </div>
                  <span className="text-[10px] uppercase tracking-widest mt-2 text-indigo-400 font-bold">Step {s}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="animate-fade-in space-y-12">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
              <h3 className="text-3xl font-serif text-slate-50 flex items-center gap-3">Cosmic Scheduling</h3>
              <div className="flex items-center gap-2 text-amber-400/80 bg-amber-400/5 px-4 py-2 border border-amber-500/20 rounded-full text-xs uppercase tracking-widest font-bold">
                <ShieldCheck size={14} className="text-emerald-500" /> Secure Payment Required
              </div>
            </div>

            <div className="space-y-6">
              <h4 className="text-indigo-400 uppercase tracking-[0.3em] text-[10px] font-bold">1. Choose Your Alignments</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {SERVICES.map(s => (
                  <button key={s.id} onClick={() => toggleService(s.id)} className={`flex items-center justify-between p-4 border transition-all rounded-sm text-left ${booking.serviceIds.includes(s.id) ? 'bg-amber-500 border-amber-500 text-slate-950 shadow-xl' : 'bg-slate-900 border-indigo-900/50 text-indigo-100 hover:border-indigo-400'}`}>
                    <div className="flex flex-col"><span className="text-xs font-serif font-bold">{s.title}</span><span className={`text-[9px] uppercase tracking-widest mt-1 ${booking.serviceIds.includes(s.id) ? 'text-slate-800' : 'text-indigo-400'}`}>${s.price}</span></div>
                    {booking.serviceIds.includes(s.id) ? <XCircle size={16} /> : <PlusCircle size={16} />}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-6 pt-8 border-t border-indigo-900/30">
              <h4 className="text-indigo-400 uppercase tracking-[0.3em] text-[10px] font-bold">2. Communication Gateway</h4>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { id: 'video', label: 'Video Call', icon: Video },
                  { id: 'phone', label: 'Phone Call', icon: PhoneCall },
                  { id: 'message', label: 'Message Chat', icon: MessageSquare }
                ].map(method => (
                  <button key={method.id} onClick={() => setBooking({ ...booking, commMethod: method.id as any })} className={`p-6 border transition-all rounded-sm flex flex-col items-center gap-4 ${booking.commMethod === method.id ? 'bg-indigo-600 border-indigo-400 text-white shadow-xl' : 'bg-slate-900 border-indigo-900/50 text-indigo-400 hover:border-amber-500/50'}`}>
                    <method.icon size={24} /><span className="text-[10px] uppercase font-bold tracking-widest">{method.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-12 pt-8 border-t border-indigo-900/30">
              <div className="space-y-6">
                <h4 className="text-indigo-400 uppercase tracking-[0.3em] text-[10px] font-bold">3. Select Your Date</h4>
                <div className="bg-indigo-950/30 p-4 border border-indigo-900/50 rounded-sm">
                  <div className="flex justify-between items-center mb-4 px-2">
                    <h4 className="font-serif text-indigo-100">{monthData.monthName} {monthData.year}</h4>
                    <div className="flex gap-2">
                      <button onClick={() => setViewDate(new Date(monthData.year, monthData.month - 1, 1))} className="p-1 text-indigo-400 hover:text-amber-400 transition-colors"><ChevronLeft size={20}/></button>
                      <button onClick={() => setViewDate(new Date(monthData.year, monthData.month + 1, 1))} className="p-1 text-indigo-400 hover:text-amber-400 transition-colors"><ChevronRight size={20}/></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center mb-2">{['S','M','T','W','T','F','S'].map(d => <span key={d} className="text-[10px] text-indigo-500 font-bold">{d}</span>)}</div>
                  <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: monthData.firstDayIndex }).map((_, i) => (<div key={`pad-${i}`} className="aspect-square" />))}
                    {monthData.daysArray.map(d => (
                      <button key={d} disabled={isPast(d)} onClick={() => setBooking({...booking, date: new Date(monthData.year, monthData.month, d)})} className={`aspect-square text-xs flex items-center justify-center border transition-all rounded-sm ${isSelected(d) ? 'bg-amber-500 border-amber-500 text-slate-950 font-bold' : isPast(d) ? 'opacity-20 cursor-not-allowed border-transparent' : 'border-transparent hover:border-amber-500 text-indigo-200'} ${isToday(d) && !isSelected(d) ? 'ring-1 ring-amber-500/50' : ''}`}>{d}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <h4 className="text-indigo-400 uppercase tracking-[0.3em] text-[10px] font-bold">4. Confirm Time</h4>
                <div className="grid grid-cols-2 gap-3">
                  {TIME_SLOTS.map((slot) => (
                    <button key={slot.time} disabled={!slot.available} onClick={() => setBooking({...booking, time: slot.time})} className={`py-3 px-4 border text-sm transition-all rounded-sm ${!slot.available ? 'opacity-30 cursor-not-allowed border-indigo-950' : booking.time === slot.time ? 'bg-amber-500 border-amber-500 text-slate-950 font-bold' : 'border-indigo-900 text-indigo-100 hover:border-amber-400'}`}>{slot.time}</button>
                  ))}
                </div>
                
                <div className="mt-8 p-6 bg-amber-500/5 border border-amber-500/20 rounded-sm">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[10px] text-indigo-400 uppercase tracking-widest font-bold">Summary</span>
                    <div className="text-right">
                      {discountPercent > 0 && <span className="block text-[9px] text-emerald-400 font-bold">-{discountPercent * 100}% Discount</span>}
                      <span className="text-amber-500 font-bold text-lg">${finalPrice.toFixed(0)}</span>
                    </div>
                  </div>
                  <button disabled={booking.serviceIds.length === 0 || !booking.time || !booking.date} onClick={() => setStep(2)} className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white uppercase tracking-widest text-xs font-bold transition-all shadow-lg rounded-sm">Continue Alignment</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-in space-y-6">
            <h3 className="text-3xl font-serif text-slate-50 mb-8">Registry Signature</h3>
            <form onSubmit={(e) => { e.preventDefault(); setStep(3); }} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-xs uppercase tracking-widest text-indigo-400 mb-2 font-bold">Full Name *</label>
                  <input type="text" required value={booking.name} onChange={e => setBooking({...booking, name: e.target.value})} className="w-full bg-slate-900 border border-indigo-900 p-4 text-indigo-100 focus:outline-none focus:border-amber-500 rounded-sm" />
                </div>
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-xs uppercase tracking-widest text-indigo-400 mb-2 font-bold">Email Address *</label>
                  <input type="email" required value={booking.email} onChange={e => setBooking({...booking, email: e.target.value})} className="w-full bg-slate-900 border border-indigo-900 p-4 text-indigo-100 focus:outline-none focus:border-amber-500 rounded-sm" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs uppercase tracking-widest text-indigo-400 mb-2 flex items-center gap-2 font-bold"><Phone size={12}/> Phone Number *</label>
                  <input type="tel" required value={booking.phone} onChange={e => setBooking({...booking, phone: e.target.value})} className="w-full bg-slate-900 border border-indigo-900 p-4 text-indigo-100 focus:outline-none focus:border-amber-500 rounded-sm" />
                </div>
              </div>
              <div className="flex gap-4 pt-6">
                <button type="button" onClick={() => setStep(1)} className="flex-1 py-4 border border-indigo-800 text-indigo-200 uppercase tracking-widest text-sm hover:text-white rounded-sm">Back</button>
                <button type="submit" className="flex-2 w-full py-4 bg-amber-500 text-slate-950 font-bold uppercase tracking-widest text-sm hover:bg-amber-400 rounded-sm">Review Session</button>
              </div>
            </form>
          </div>
        )}

        {step === 3 && (
          <div className="animate-fade-in text-center">
            <h3 className="text-3xl font-serif text-slate-50 mb-8">Secure Payment</h3>
            <div className="bg-indigo-950/30 p-8 border border-indigo-900/50 text-left mb-6 max-w-lg mx-auto rounded-sm">
              <div className="space-y-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-indigo-400 uppercase tracking-widest text-[9px] font-bold">Method</span>
                  <span className="text-white font-medium capitalize">{booking.commMethod} Session</span>
                </div>
                <div className="flex justify-between items-center text-sm border-t border-indigo-900/50 pt-4">
                  <span className="text-amber-400 font-bold text-3xl font-serif">Total</span>
                  <span className="text-amber-400 font-bold text-3xl font-serif">${finalPrice.toFixed(0)}</span>
                </div>
              </div>
              <div className="mt-8">
                <PayPalButtons amount={finalPrice} description={`Astrology Reading for ${booking.name}`} onSuccess={handlePaymentSuccess} onError={() => alert("Payment error.")} />
              </div>
            </div>
            <button disabled={isSubmitting} onClick={() => setStep(2)} className="max-w-lg mx-auto w-full py-4 border border-indigo-800 text-indigo-200 uppercase tracking-widest text-xs hover:text-white rounded-sm">Modify Details</button>
          </div>
        )}

        {step === 4 && (
          <div className="animate-fade-in text-center py-12">
            <CheckCircle size={64} className="text-emerald-500 mx-auto mb-6" />
            <h3 className="text-4xl font-serif text-slate-50 mb-4">Alignment Secured</h3>
            <p className="text-indigo-200/80 mb-6 max-w-sm mx-auto leading-relaxed">Your session is booked via {booking.commMethod}. Allie will initiate the channel at your selected time.</p>
            <button onClick={() => setStep(1)} className="px-8 py-3 border border-indigo-800 text-indigo-300 hover:text-white uppercase tracking-widest text-[10px] rounded-sm">Return Home</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Booking;
